import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HANDOFF_MS,
  idleFeedback,
  startFeedback,
  handoffFeedback,
  failFeedback,
  resetFeedback,
  expireFeedback,
} from '../utils/downloadFeedback';

/**
 * Download button phase: 'idle' | 'preparing' | 'handoff'.
 * start() returns a generation id, or null while a download is already running
 * (checked on a ref, so a double click in the same tick cannot slip through).
 */
export default function useDownloadFeedback() {
  const ref = useRef(idleFeedback());
  const timerRef = useRef(null);
  const [phase, setPhase] = useState('idle');

  const apply = useCallback((next) => {
    ref.current = next;
    setPhase(next.phase);
  }, []);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const start = useCallback(() => {
    const next = startFeedback(ref.current);
    if (next === ref.current) return null;
    apply(next);
    return next.gen;
  }, [apply]);

  const handoff = useCallback(
    (gen) => {
      const next = handoffFeedback(ref.current, gen, Date.now());
      if (next === ref.current) return;
      apply(next);
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        apply(expireFeedback(ref.current, ref.current.until));
      }, HANDOFF_MS);
    },
    [apply],
  );

  const fail = useCallback((gen) => apply(failFeedback(ref.current, gen)), [apply]);

  const reset = useCallback(() => {
    clearTimer();
    apply(resetFeedback(ref.current));
  }, [apply]);

  useEffect(() => clearTimer, []);

  return { phase, start, handoff, fail, reset };
}
